// src/pages/LoginPage.jsx
import React, { useState, useEffect } from "react";
import { Box, TextField, Button, Typography, Container, Paper, Divider, Alert, IconButton, InputAdornment } from "@mui/material";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { API_BASE_URL } from "../config/apiKey.js";
import { motion } from "framer-motion";
import GoogleIcon from "@mui/icons-material/Google";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithGoogle, logout, isAuthenticated, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [unlockMessage, setUnlockMessage] = useState("");
  const [concurrentLoginError, setConcurrentLoginError] = useState(null);
  const [logoutRequiredError, setLogoutRequiredError] = useState(null);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);

  // Check for already_logged_in error from Google OAuth
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("error") === "already_logged_in") {
      setLogoutRequiredError({
        message: params.get("message") || "You are already logged in on another device.",
        isGoogle: true
      });
      const callbackEmail = params.get("email");
      if (callbackEmail) setEmail(callbackEmail);
    }
  }, [location.search]);

  // Show logout option if already authenticated instead of redirecting immediately
  // This gives users a choice to logout or go to dashboard
  useEffect(() => {
    if (isAuthenticated) {
      // If we are already authenticated, we redirect to dashboard
      // BUT if there was an error in the URL, we might want to show it?
      // Usually, if we have a token, we are good.
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Check for unlock notification from query params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("unlocked") === "true") {
      const unlockedEmail = params.get("email");
      if (unlockedEmail) {
        setEmail(unlockedEmail);
        setUnlockMessage(unlockedEmail);
        // Clear the query params from URL
        navigate("/login", { replace: true });
      }
    }

    // Check for concurrent login limit from Google OAuth
    if (params.get("error") === "concurrent_login_limit") {
      setConcurrentLoginError({
        message: `You have reached your maximum concurrent login limit of ${params.get("max") || 1}. Please log out from another device.`,
        maxLogins: params.get("max") || 1,
        currentLogins: params.get("current") || 1,
        isGoogle: true,
      });
      // Try to get email if passed from callback
      const callbackEmail = params.get("email");
      if (callbackEmail) setEmail(callbackEmail);
      
      // Clear the query params
      navigate("/login", { replace: true });
    }

  }, [location.search, navigate]);

  // Show logout option if already authenticated
  // If a token exists, we MUST show the logout option prominently to avoid session conflicts
  const showAlreadyLoggedIn = false; // We redirect to dashboard now if authenticated

  const handleLogout = async () => {
    setLoading(true);
    setError("");
    setLogoutRequiredError(null);
    setConcurrentLoginError(null);
    
    try {
      await logout();
      setError("✅ Logged out successfully. You can now log in again.");
      
      // Force a complete reload of the login page state
      setEmail("");
      setPassword("");
      
      // Short delay to ensure state updates propagate
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 100);
    } catch (err) {
      setError("Failed to logout. Please try again or clear your browser data.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const result = await login(email, password);
    if (result.success) {
      // Check if user came from pricing page
      const from = location.state?.from;
      const planId = location.state?.planId;
      if (from === "/pricing") {
        // Redirect back to pricing page after login
        navigate("/pricing");
      } else {
      navigate("/dashboard");
      }
    } else {
      // Handle "needs logout" error
      if (result.needsLogout) {
        setLogoutRequiredError({
          message: result.message,
          isGoogle: false
        });
        setError("");
        setLoading(false);
        return;
      }

      // Handle account locked - redirect to unlock request page
      if (result.error === "ACCOUNT_LOCKED" || result.error === "USER_BLOCKED") {
        // If account is blocked, we should logout first to clear any partial session
        logout();
        setError(result.message || "your account has been blocked kindly logout and contact admin");
        setLoading(false);
        // Optionally navigate after a delay or show a specific UI
        // navigate(`/account-locked?email=${encodeURIComponent(email)}`);
        return;
      }
      // Handle password reset required - redirect to reset password page
      if (result.error === "PASSWORD_RESET_REQUIRED") {
        navigate(`/reset-password-required?email=${encodeURIComponent(email)}`);
        return;
      }
      // Handle concurrent login limit
      if (result.error === "CONCURRENT_LOGIN_LIMIT_REACHED") {
        setConcurrentLoginError({
          message: result.message || "You have reached your maximum concurrent login limit.",
          maxLogins: result.maxLogins || 1,
          currentLogins: result.currentLogins || 1,
        });
        setError(""); // Clear regular error
        setLoading(false);
        return;
      }
      // Display message if available (e.g., remaining attempts), otherwise show error
      setError(result.message || result.error || "Login failed");
      setConcurrentLoginError(null); // Clear concurrent login error
    }
    setLoading(false);
  };

  const handleLogoutAll = async (isFromConflict = false) => {
    // If it's a Google error, we might not have a password
    const isGoogleError = new URLSearchParams(location.search).get("error") === "concurrent_login_limit" || 
                         concurrentLoginError?.isGoogle || 
                         (isFromConflict && logoutRequiredError?.isGoogle);

    if (!email || (!password && !isGoogleError)) {
      setError("Please enter your email and password to logout from all devices.");
      return;
    }

    setLogoutAllLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/logout-all`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password: password || "" }),
      });

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Non-JSON response received:", text.substring(0, 200));
        setError("Failed to logout from all devices. Please try again.");
        setLogoutAllLoading(false);
        return;
      }

      const data = await response.json();
      if (response.ok) {
        setConcurrentLoginError(null);
        setError("");
        // Show success message and try to login again
        setError("✅ Logged out from all devices. Please try logging in again.");
        // Auto-retry login after a short delay
        setTimeout(() => {
          handleSubmit(new Event("submit"));
        }, 1000);
      } else {
        setError(data.error || "Failed to logout from all devices. Please check your credentials.");
      }
    } catch (error) {
      console.error("Logout all error:", error);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setLogoutAllLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setLogoutRequiredError(null);
    loginWithGoogle();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background Effects */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            radial-gradient(#1e3a8a 0.5px, transparent 0.5px),
            radial-gradient(#1e3a8a 0.5px, #ffffff 0.5px)
          `,
          backgroundSize: "40px 40px",
          backgroundPosition: "0 0, 20px 20px",
          opacity: 0.03,
        }}
      />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        {/* Back Button */}
        <Box sx={{ mb: 2 }}>
          <IconButton
            onClick={() => navigate("/")}
            sx={{
              color: "#1e3a8a",
              bgcolor: "rgba(30, 58, 138, 0.05)",
              "&:hover": {
                bgcolor: "rgba(30, 58, 138, 0.1)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              bgcolor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 4,
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            }}
          >
            {/* Logo */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1.5,
                  bgcolor: "rgba(30, 58, 138, 0.05)",
                  borderRadius: 999,
                  px: 2,
                  py: 1,
                  mb: 2,
                  border: "1px solid rgba(30, 58, 138, 0.1)",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    bgcolor: "#1e3a8a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ color: "white", fontWeight: "bold", fontSize: "20px" }}>
                    EXI
                  </Typography>
                </Box>
                <Typography variant="h5" sx={{ color: "#1e3a8a", fontWeight: "800" }}>
                  exivis
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: "#0f172a", fontWeight: 800, mb: 1 }}>
                Welcome Back
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                Sign in to continue to exivis
              </Typography>
            </Box>

            {unlockMessage && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 3, 
                  bgcolor: "#f0fdf4", 
                  color: "#166534",
                  border: "1px solid #bbf7d0",
                }}
                action={
                  <Button
                    size="small"
                    onClick={() => navigate(`/reset-password-required?email=${encodeURIComponent(unlockMessage)}`)}
                    sx={{
                      color: "#166534",
                      fontWeight: "bold",
                      textTransform: "none",
                    }}
                  >
                    Reset Password
                  </Button>
                }
              >
                Your account ({unlockMessage}) has been unlocked! Please reset your password to continue.
              </Alert>
            )}

            {logoutRequiredError && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 3, 
                  bgcolor: "#fffbeb", 
                  color: "#854d0e",
                  border: "1px solid #fef3c7",
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: "bold", mb: 1 }}>
                  Session Conflict Detected
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {logoutRequiredError?.message || "It seems you are already logged in on another device."}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={logoutAllLoading}
                    onClick={() => handleLogoutAll(true)}
                    sx={{
                      bgcolor: "#ef4444",
                      color: "white",
                      fontWeight: "bold",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#dc2626",
                        boxShadow: "none",
                      }
                    }}
                  >
                    {logoutAllLoading ? "Processing..." : "Logout from Other Devices"}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleLogout}
                    sx={{
                      color: "#475569",
                      borderColor: "#e2e8f0",
                      fontWeight: "bold",
                      "&:hover": {
                        borderColor: "#cbd5e1",
                        bgcolor: "#f8fafc",
                      }
                    }}
                  >
                    Clear Current Browser Session
                  </Button>
                </Box>
              </Alert>
            )}

            {concurrentLoginError && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 3, 
                  bgcolor: "#f0f9ff", 
                  color: "#075985",
                  border: "1px solid #e0f2fe",
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: "bold", mb: 2, fontSize: "1rem" }}>
                  You are not logged out
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, fontSize: "0.9rem", color: "#0369a1" }}>
                  Your account is already active on another device. If you want to logout, click the button below.
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleLogoutAll}
                  disabled={logoutAllLoading || !email || (!password && !concurrentLoginError?.isGoogle)}
                  sx={{
                    mt: 1,
                    bgcolor: "#1e3a8a",
                    color: "white",
                    fontWeight: "bold",
                    textTransform: "none",
                    py: 1.5,
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: "#1e40af",
                      boxShadow: "none",
                    },
                  }}
                >
                  {logoutAllLoading ? "Logging out..." : "Logout from All Devices"}
                </Button>
              </Alert>
            )}
            {error && !concurrentLoginError && !logoutRequiredError && (
              <Alert severity="error" sx={{ mb: 3, bgcolor: "#fef2f2", color: "#991b1b", border: "1px solid #fee2e2" }}>
                {(() => {
                  if (typeof error === "string") return error;
                  if (error?.message && typeof error.message === "string") return error.message;
                  if (error?.error && typeof error.error === "string") return error.error;
                  try {
                    return JSON.stringify(error);
                  } catch {
                    return "An error occurred";
                  }
                })()}
              </Alert>
            )}

            {showAlreadyLoggedIn ? (
              // ... skipping already logged in content as it's set to false anyway ...
              null
            ) : (
              <>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a8a",
                    },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: "#64748b" }}
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#f8fafc",
                    "& fieldset": {
                      borderColor: "#e2e8f0",
                    },
                    "&:hover fieldset": {
                      borderColor: "#cbd5e1",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1e3a8a",
                    },
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: "#1e3a8a",
                  color: "white",
                  py: 1.5,
                  mb: 2,
                  fontSize: "16px",
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: "12px",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#1e40af",
                    boxShadow: "none",
                  },
                }}
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <Divider sx={{ my: 3, borderColor: "#f1f5f9" }}>
              <Typography variant="body2" sx={{ color: "#94a3b8", px: 2, fontWeight: 600 }}>
                OR
              </Typography>
            </Divider>

            <Button
              fullWidth
              variant="outlined"
              onClick={handleGoogleLogin}
              startIcon={<GoogleIcon />}
              sx={{
                borderColor: "#e2e8f0",
                color: "#475569",
                py: 1.5,
                mb: 3,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                "&:hover": {
                  borderColor: "#cbd5e1",
                  bgcolor: "#f8fafc",
                },
              }}
            >
              Continue with Google
            </Button>

            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                Don't have an account?{" "}
                <Link
                  to="/signup"
                  style={{
                    color: "#1e3a8a",
                    textDecoration: "none",
                    fontWeight: 700,
                  }}
                >
                  Sign Up
                </Link>
              </Typography>
            </Box>
            </>
            )}
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
}

